import { env } from '@/config/env';
import { deviceIdentity } from '@/api/deviceIdentity';
import { DEFAULT_ENVELOPE_MODE, isApiEnvelope } from '@/api/apiTypes';
import type { EnvelopeMode } from '@/api/apiTypes';
import { API_VERSION, APP_VERSION, SYNC_SCHEMA_VERSION } from '@/config/appVersion';
import { ApiError } from '@/api/ApiError';
import { notifyHealthyResponse, reportCompatibilityError } from '@/api/appCompatibility';
import { reportBillingGateError } from '@/api/billingGate';
import type { AuthTokens, BackendLoginResponse } from '@/types/auth';

const accessTokenKey = 'fuelops.access_token';
const refreshTokenKey = 'fuelops.refresh_token';
export const authSessionExpiredEvent = 'fuelops:auth-session-expired';

let refreshPromise: Promise<AuthTokens | null> | null = null;

type RequestOptions = RequestInit & {
  skipAuth?: boolean;
  skipRefresh?: boolean;
  responseType?: 'json' | 'blob';
  /** How the JSON body is interpreted. Defaults to `auto` (legacy duck-typed behavior). */
  envelope?: EnvelopeMode;
};

export type FileDownloadResponse = {
  blob: Blob;
  headers: Headers;
};

export const tokenStorage = {
  getTokens(): AuthTokens | null {
    const accessToken = window.localStorage.getItem(accessTokenKey);
    const refreshToken = window.localStorage.getItem(refreshTokenKey);

    if (!accessToken || !refreshToken) {
      return null;
    }

    return { accessToken, refreshToken };
  },
  getRefreshToken() {
    return window.localStorage.getItem(refreshTokenKey);
  },
  setTokens(tokens: AuthTokens) {
    // TODO: Prefer HTTP-only secure cookies in production if the Django backend supports them.
    window.localStorage.setItem(accessTokenKey, tokens.accessToken);
    window.localStorage.setItem(refreshTokenKey, tokens.refreshToken);
  },
  clearTokens() {
    window.localStorage.removeItem(accessTokenKey);
    window.localStorage.removeItem(refreshTokenKey);
  }
};

const notifySessionExpired = () => {
  window.dispatchEvent(new Event(authSessionExpiredEvent));
};

// Version negotiation headers (phase C.2). The three travel together as a set
// on every request so the backend can reason about client/contract/sync skew.
const APP_VERSION_HEADER = 'X-App-Version';
const API_VERSION_HEADER = 'X-Api-Version';
const SYNC_SCHEMA_VERSION_HEADER = 'X-Sync-Schema-Version';

// The compatibility-check endpoint is excluded from version enforcement on the
// backend; the client must not send the headers there either (avoids a chicken
// -and-egg where a rejected version blocks the very call that diagnoses it).
// Exported so the version-policy client (phase C.3) targets the exact same path
// that this module excludes — single source of truth for the endpoint.
export const VERSION_CHECK_PATH = '/api/v1/app/version/';

const targetsVersionCheckEndpoint = (path: string): boolean => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  const [pathWithoutQuery] = normalizedPath.split(/[?#]/, 1);
  return pathWithoutQuery === VERSION_CHECK_PATH;
};

/**
 * Stamps the three version headers on an outgoing request. Invariant: the
 * request ends up with either all three (every normal call) or none (the
 * version-check endpoint) — never a partial subset. Headers the caller set
 * explicitly are left untouched, so a custom override always wins.
 */
const applyVersionHeaders = (headers: Headers, path: string): void => {
  if (targetsVersionCheckEndpoint(path)) {
    return;
  }

  if (!headers.has(APP_VERSION_HEADER)) {
    headers.set(APP_VERSION_HEADER, APP_VERSION);
  }
  if (!headers.has(API_VERSION_HEADER)) {
    headers.set(API_VERSION_HEADER, API_VERSION);
  }
  if (!headers.has(SYNC_SCHEMA_VERSION_HEADER)) {
    headers.set(SYNC_SCHEMA_VERSION_HEADER, String(SYNC_SCHEMA_VERSION));
  }
};

const buildUrl = (path: string) => {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (env.apiBaseUrl.endsWith('/api/v1') && normalizedPath.startsWith('/api/v1/')) {
    return `${env.apiBaseUrl}${normalizedPath.replace('/api/v1', '')}`;
  }

  return `${env.apiBaseUrl}${normalizedPath}`;
};

const parseJson = async <T>(
  response: Response,
  mode: EnvelopeMode = DEFAULT_ENVELOPE_MODE
): Promise<T> => {
  if (response.status === 204) {
    return undefined as T;
  }

  const payload = (await response.json()) as unknown;

  // `raw` never inspects the shape: return the parsed JSON as-is.
  if (mode === 'raw') {
    return payload as T;
  }

  if (isApiEnvelope<T>(payload)) {
    if (!payload.success) {
      throw new Error('API request failed');
    }

    return payload.data as T;
  }

  // `unwrap` requires an envelope; a flat payload here is a contract violation.
  if (mode === 'unwrap') {
    throw new Error('Expected an enveloped API response');
  }

  // `auto`: flat payloads (platform endpoints, plain responses) pass through.
  return payload as T;
};

const stringifyApiError = (payload: unknown): string | undefined => {
  if (!payload) {
    return undefined;
  }

  if (typeof payload === 'string') {
    return payload;
  }

  if (Array.isArray(payload)) {
    return payload.map(stringifyApiError).filter(Boolean).join(' ');
  }

  if (typeof payload === 'object') {
    const record = payload as Record<string, unknown>;
    const directMessage = record.detail ?? record.message ?? record.error;

    if (directMessage) {
      return stringifyApiError(directMessage);
    }

    return Object.entries(record)
      .map(([key, value]) => {
        const message = stringifyApiError(value);
        return message ? `${key}: ${message}` : undefined;
      })
      .filter(Boolean)
      .join(' ');
  }

  return undefined;
};

const hasTokenNotValidCode = (payload: unknown): boolean => {
  if (!payload || typeof payload !== 'object') {
    return false;
  }

  const record = payload as Record<string, unknown>;

  if (record.code === 'token_not_valid') {
    return true;
  }

  return hasTokenNotValidCode(record.errors);
};

const isTokenNotValidResponse = async (response: Response) => {
  try {
    const contentType = response.headers.get('Content-Type') ?? '';

    if (!contentType.includes('application/json')) {
      return false;
    }

    const payload = (await response.clone().json()) as unknown;
    return hasTokenNotValidCode(payload);
  } catch {
    return false;
  }
};

const extractErrorCode = (payload: unknown): string | null => {
  if (payload && typeof payload === 'object') {
    const record = payload as Record<string, unknown>;

    // Top-level `code` wins (flat platform responses: 426/423, version-header 400).
    if (typeof record.code === 'string') {
      return record.code;
    }

    // Enveloped detail errors carry the machine code under `errors.code`
    // (e.g. the billing gating 403 `{ errors: { detail, code } }`).
    const errors = record.errors;
    if (errors && typeof errors === 'object' && !Array.isArray(errors)) {
      const nested = (errors as Record<string, unknown>).code;
      if (typeof nested === 'string') {
        return nested;
      }
    }
  }
  return null;
};

const extractErrorDetails = (payload: unknown): unknown =>
  payload && typeof payload === 'object' ? (payload as Record<string, unknown>).details : undefined;

/**
 * Lifts the envelope's `errors` map into a normalized DRF-style field→messages
 * record. Returns `null` when `errors` is absent or not a field map — e.g. a
 * 401 `{ detail, code }` (skipped via the string `detail`), so callers can tell
 * "validation failed on these fields" from "auth/other error".
 */
const extractFieldErrors = (payload: unknown): Record<string, string[]> | null => {
  if (!payload || typeof payload !== 'object') {
    return null;
  }

  const errors = (payload as Record<string, unknown>).errors;

  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) {
    return null;
  }

  const record = errors as Record<string, unknown>;

  // 401 auth errors use `{ detail, code }`, not a field map — don't misread them.
  if (typeof record.detail === 'string') {
    return null;
  }

  const result: Record<string, string[]> = {};

  for (const [key, value] of Object.entries(record)) {
    if (Array.isArray(value)) {
      result[key] = value.map(String);
    } else if (typeof value === 'string') {
      result[key] = [value];
    }
  }

  return Object.keys(result).length > 0 ? result : null;
};

/**
 * Builds the structured {@link ApiError} for a failed response. Reads the body
 * once to flatten a human `message` (legacy behavior) and to lift the backend's
 * machine `code`/`details`. The `Response` is retained for header access
 * (e.g. `Retry-After`) even though its body is now consumed.
 */
const buildApiError = async (response: Response): Promise<ApiError> => {
  const fallback = `API request failed with status ${response.status}`;
  let message = fallback;
  let code: string | null = null;
  let details: unknown;
  let fieldErrors: Record<string, string[]> | null = null;

  try {
    const contentType = response.headers.get('Content-Type') ?? '';

    if (contentType.includes('application/json')) {
      const payload = (await response.json()) as unknown;
      message = stringifyApiError(payload) ?? fallback;
      code = extractErrorCode(payload);
      details = extractErrorDetails(payload);
      fieldErrors = extractFieldErrors(payload);
    } else {
      const text = await response.text();
      message = text || fallback;
    }
  } catch {
    // Keep the fallback message when the body is missing or unparsable.
  }

  return new ApiError({ status: response.status, code, message, details, fieldErrors, response });
};

/**
 * Turns a failed response into an {@link ApiError} and runs it through the
 * version/maintenance interceptor (phase C.5) before it is thrown, so a 426/423/
 * 503 reaches the global handler even though the caller only sees the throw.
 */
const rejectResponse = async (response: Response): Promise<ApiError> => {
  const error = await buildApiError(response);
  reportCompatibilityError(error);
  reportBillingGateError(error);
  return error;
};

/**
 * Signals "backend reachable" after a successful request so the maintenance
 * banner can clear itself. Skips the fail-open version-check endpoint, whose 2xx
 * does not prove the enforced API recovered.
 */
const noteSuccessfulResponse = (path: string): void => {
  if (!targetsVersionCheckEndpoint(path)) {
    notifyHealthyResponse();
  }
};

const normalizeTokens = (response: BackendLoginResponse): AuthTokens => ({
  accessToken: response.access_token ?? response.access ?? '',
  refreshToken: response.refresh_token ?? response.refresh ?? ''
});

const refreshTokens = async () => {
  const tokens = tokenStorage.getTokens();

  if (!tokens?.refreshToken) {
    return null;
  }

  const refreshHeaders = new Headers({ 'Content-Type': 'application/json' });
  applyVersionHeaders(refreshHeaders, '/api/v1/auth/refresh/');

  refreshPromise ??= fetch(buildUrl('/api/v1/auth/refresh/'), {
    method: 'POST',
    headers: refreshHeaders,
    body: JSON.stringify({
      refresh: tokens.refreshToken,
      device_id: deviceIdentity.getDeviceId()
    })
  })
    .then(async (response) => {
      if (!response.ok) {
        tokenStorage.clearTokens();
        notifySessionExpired();
        return null;
      }

      const payload = await parseJson<BackendLoginResponse>(response);
      const nextTokens = normalizeTokens(payload);

      if (!nextTokens.accessToken || !nextTokens.refreshToken) {
        tokenStorage.clearTokens();
        notifySessionExpired();
        return null;
      }

      tokenStorage.setTokens(nextTokens);
      return nextTokens;
    })
    .finally(() => {
      refreshPromise = null;
    });

  return refreshPromise;
};

export const httpClient = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const {
    skipAuth,
    skipRefresh,
    responseType = 'json',
    envelope = DEFAULT_ENVELOPE_MODE,
    headers,
    ...requestOptions
  } = options;
  const tokens = tokenStorage.getTokens();
  const requestHeaders = new Headers(headers);

  if (
    !requestHeaders.has('Content-Type') &&
    requestOptions.body &&
    !(requestOptions.body instanceof FormData)
  ) {
    requestHeaders.set('Content-Type', 'application/json');
  }

  if (!skipAuth && tokens?.accessToken) {
    requestHeaders.set('Authorization', `Bearer ${tokens.accessToken}`);
  }

  // Stamp before the first fetch so the post-refresh retry inherits them too
  // (retryHeaders is cloned from requestHeaders).
  applyVersionHeaders(requestHeaders, path);

  const response = await fetch(buildUrl(path), {
    ...requestOptions,
    headers: requestHeaders
  });

  if (
    response.status === 401 &&
    !skipAuth &&
    !skipRefresh &&
    (await isTokenNotValidResponse(response))
  ) {
    const nextTokens = await refreshTokens();

    if (nextTokens?.accessToken) {
      const retryHeaders = new Headers(requestHeaders);
      retryHeaders.set('Authorization', `Bearer ${nextTokens.accessToken}`);

      const retryResponse = await fetch(buildUrl(path), {
        ...requestOptions,
        headers: retryHeaders
      });

      if (retryResponse.ok) {
        noteSuccessfulResponse(path);

        if (responseType === 'blob') {
          return {
            blob: await retryResponse.blob(),
            headers: retryResponse.headers
          } as T;
        }

        return parseJson<T>(retryResponse, envelope);
      }

      if (retryResponse.status === 401 && (await isTokenNotValidResponse(retryResponse))) {
        tokenStorage.clearTokens();
        notifySessionExpired();
      }

      throw await rejectResponse(retryResponse);
    }
  }

  if (!response.ok) {
    throw await rejectResponse(response);
  }

  noteSuccessfulResponse(path);

  if (responseType === 'blob') {
    return {
      blob: await response.blob(),
      headers: response.headers
    } as T;
  }

  return parseJson<T>(response, envelope);
};

export const authTokenUtils = {
  normalizeTokens,
  tokenStorage
};
